// ensure React is imported in the file to avoid "React is not defined" error
import React, { useMemo, useContext, useState, useEffect } from "react";

type BCRoutingHistory = typeof window.history;
type BCRoutingLocation = typeof window.location;
type BCRoutingNavigate = typeof window.open;

(function () {
  const pushState = history.pushState;
  history.pushState = function (...args) {
    pushState.apply(history, args);
    window.dispatchEvent(new Event("locationchange"));
  };

  const replaceState = history.replaceState;
  history.replaceState = function (...args) {
    replaceState.apply(history, args);
    window.dispatchEvent(new Event("locationchange"));
  };

  window.addEventListener("popstate", () => {
    window.dispatchEvent(new Event("locationchange"));
  });
})();

export const useLocationParams = () => {
  const [locationState, setLocationState] = useState<URLSearchParams>();

  useEffect(() => {
    const updateVariant = () => {
      const locationParams = new URLSearchParams(window.location.search);
      setLocationState(locationParams);
    };

    // Update variant initially
    updateVariant();

    // Listen for our custom location change event
    window.addEventListener("locationchange", updateVariant);

    // Cleanup on unmount
    return () => {
      window.removeEventListener("locationchange", updateVariant);
    };
  }, []);

  return locationState;
};

export interface BCRoutingContextValue {
  history: BCRoutingHistory;
  location: BCRoutingLocation;
  navigate: BCRoutingNavigate;
}

export const BCRoutingContext =
  React.createContext<BCRoutingContextValue | null>(null);

export const useBCRouting = () => {
  const context = useContext(BCRoutingContext);
  if (!context) {
    throw new Error(
      "useBCRouting must be used within a BCRoutingContextProvider"
    );
  }
  return context;
};

export const BCRoutingContextProvider = ({
  children,
}: React.PropsWithChildren) => {
  const value = useMemo(
    () => ({
      get history() {
        return window.history;
      },
      get location() {
        return window.location;
      },
      navigate(...args: Parameters<typeof window.open>) {
        return window.open(...args);
      },
    }),
    []
  );

  return (
    <BCRoutingContext.Provider value={value}>
      {children}
    </BCRoutingContext.Provider>
  );
};
